"use client";

import React from "react";
import { ArrowUpRight, BookOpen, CircleQuestionMark as HelpCircle, Workflow, Hammer, Bath, UtensilsCrossed, House as Home, Layers, SunMedium, Trees, Maximize2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
interface ServiceItem {
  title: string;
  description: string;
  path: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  icon: React.ElementType;
}
interface ResourceItem {
  title: string;
  description: string;
  path: string;
  icon: React.ElementType;
}
const services: ServiceItem[] = [{
  title: "Kitchen Remodeling",
  description: "Custom cabinetry, oversized quartz islands, luxury cooktop alcoves, and bespoke chef pantries.",
  path: "/KitchenRemodelingPage",
  icon: UtensilsCrossed,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_island.png-wvc-sizes",
    alt: "Renovated Brookhaven gourmet kitchen featuring custom white oak island and brass pendant lighting"
  }
}, {
  title: "Bathroom Remodeling",
  description: "Spa-grade primary suites with zero-threshold curbless showers, freestanding soaking tubs, and heated floors.",
  path: "/BathroomRemodelingPage",
  icon: Bath,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_bathroom.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_bathroom.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_bathroom.png-wvc-sizes",
    alt: "Modern luxury master bathroom with marble tile walk-in shower and freestanding soaking tub"
  }
}, {
  title: "Whole-Home Renovations",
  description: "Complete interior reconfigurations that unify floor plans, architectural trim, and functional living space.",
  path: "/WholeHomeInteriorRemodelingPage",
  icon: Home,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation.png-wvc-sizes",
    alt: "Open concept Brookhaven home renovation highlighting wide plank hardwood flooring and cohesive interior design"
  }
}, {
  title: "Home Additions",
  description: "Seamless second-story extensions, sunrooms, master wing bump-outs, and architecturally aligned footprints.",
  path: "/HomeAdditionsPage",
  icon: Maximize2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition.png-wvc-sizes",
    alt: "Finished residential home addition with custom architectural windows and seamless exterior transition"
  }
}, {
  title: "Interior Remodeling",
  description: "Custom built-ins, staircases, structural wall removals, lighting updates, and engineered flooring installations.",
  path: "/InteriorRemodelingPage",
  icon: Layers,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living.png-wvc-sizes",
    alt: "Spacious renovated family living room featuring floor-to-ceiling fireplace hearth and bespoke millwork"
  }
}, {
  title: "Basement Remodeling",
  description: "Moisture-controlled entertainment suites, full home bars, guest suites, home gyms, and wine cellars.",
  path: "/BasementRemodelingPage",
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_finished_basement.png-wvc-sizes",
    alt: "Finished residential basement remodel with integrated wet bar and comfortable lounge seating"
  }
}, {
  title: "Outdoor Living",
  description: "Covered lanai spaces, integrated outdoor kitchen stations, flagstone patio pavilions, and custom fire features.",
  path: "/OutdoorLivingPage",
  icon: SunMedium,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_living.png-wvc-sizes",
    alt: "Covered outdoor living patio with cedar beam ceiling, outdoor stone fireplace, and dining arrangement"
  }
}, {
  title: "Deck Remodeling",
  description: "Composite low-maintenance deck builds, cable rail systems, multi-tiered platforms, and dry space systems below.",
  path: "/DeckRemodelingPage",
  icon: Trees,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck.png-wvc-sizes",
    alt: "Custom timber deck construction with modern black aluminum railings overlooking wooded backyard"
  }
}];
const resources: ResourceItem[] = [{
  title: "Remodeling Cost Guide",
  description: "Realistic budget breakdowns, regional pricing metrics, and material tiers for residential renovation projects.",
  path: "/BlogPage",
  icon: BookOpen
}, {
  title: "Our Proven Process",
  description: "Step-by-step walkthrough of how we guide your renovation from feasibility and schematic design to final handover.",
  path: "/AboutPage",
  icon: Workflow
}, {
  title: "Frequently Asked Questions",
  description: "Clear answers concerning Brookhaven permitting, project timelines, dust control, and structural engineering.",
  path: "/ContactRequestAnEstimatePage",
  icon: HelpCircle
}];
export default function ServicesDirectory() {
  return <section id="servicesandresourcesdirectory" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Directory & Resources
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground text-balance">
            Services and planning resources
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground text-balance">
            Explore our specialized residential remodeling divisions along with essential guides prepared for Brookhaven homeowners.
          </p>
        </div>

        {/* 8 Services Grid */}
        <div className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl sm:text-2xl font-serif font-medium text-foreground">
              Core Renovation Services
            </h3>
            <span className="text-sm text-muted-foreground font-mono">
              08 Disciplines
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
            const IconComp = service.icon;
            return <Card key={service.title} data-index={index} className="group relative flex flex-col justify-between border border-border bg-card transition-all duration-300 hover:shadow-md hover:border-primary/50 overflow-hidden">
                  <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                    <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 flex items-center justify-center p-2 rounded-md bg-background/90 text-foreground shadow-xs backdrop-blur-xs">
                      <IconComp className="size-4 text-primary" />
                    </div>
                  </div>

                  <CardHeader className="p-5 pb-2">
                    <CardTitle className="text-lg font-serif font-semibold text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm text-muted-foreground line-clamp-3 mt-1.5 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-5 pt-3">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                      <Link to={service.path}>
                        <span>Explore Service</span>
                        <ArrowUpRight className="size-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <Separator className="my-16" />

        {/* 3 Resources Grid */}
        <div className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl sm:text-2xl font-serif font-medium text-foreground">
              Homeowner Planning Resources
            </h3>
            <span className="text-sm text-muted-foreground font-mono">
              Knowledge Base
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {resources.map((resource, index) => {
            const ResourceIcon = resource.icon;
            return <Card key={resource.title} data-index={index} className="flex flex-col justify-between border border-border bg-card p-6 transition-all duration-300 hover:border-secondary hover:shadow-sm">
                  <div>
                    <div className="size-11 rounded-md bg-muted flex items-center justify-center text-foreground mb-4">
                      <ResourceIcon className="size-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-serif font-semibold text-card-foreground mb-2">
                      {resource.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {resource.description}
                    </CardDescription>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border">
                    <Button asChild variant="ghost" size="sm" className="px-0 font-medium text-primary hover:text-primary/80">
                      <Link to={resource.path} className="inline-flex items-center gap-1.5">
                        <span>Read full guide</span>
                        <ArrowUpRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>;
          })}
          </div>
        </div>

        {/* Project Call to Action Banner */}
        <div className="rounded-xl border border-border bg-secondary text-secondary-foreground p-8 sm:p-12 lg:p-14 relative overflow-hidden shadow-xl">
          <div className="relative z-10 max-w-2xl">
            <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30 mb-4 px-3 py-1">
              Brookhaven, Georgia
            </Badge>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-secondary-foreground tracking-tight">
              Ready to elevate your home?
            </h3>
            <p className="mt-3 text-secondary-foreground/80 text-base sm:text-lg leading-relaxed">
              Schedule a comprehensive design consultation and on-site estimate with our residential construction specialists.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an Estimate
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
                <Link to="/OurProjectsPage">
                  Browse Recent Projects
                </Link>
              </Button>
            </div>
          </div>

          <div className="absolute -right-12 -bottom-16 w-80 h-80 rounded-full bg-primary/10 blur-2xl pointer-events-none" aria-hidden="true" />
        </div>
      </div>
    </section>;
}